import {Component} from 'react';
import {
  BrowserClient,
  defaultStackParser,
  makeFetchTransport,
  Scope,
  replayCanvasIntegration,
  replayIntegration,
  browserTracingIntegration
} from '@sentry/react';

/**
 * Isolated Sentry client for the HeatshrinkEditor federated module.
 *
 * Uses BrowserClient directly instead of Sentry.init() to avoid installing
 * global error handlers (window.onerror, unhandledrejection). This ensures
 * only errors originating from HeatshrinkEditor are captured, not errors
 * from the host application (e.g., Shopify).
 */
const client = new BrowserClient({
  dsn: 'https://a42d53fc486bb691dc9acb2b880a3154@o4505195236098048.ingest.sentry.io/4506542301642752',
  environment: process.env.NODE_ENV,
  transport: makeFetchTransport,
  stackParser: defaultStackParser,
  integrations: [
    replayCanvasIntegration(),
    replayIntegration({
      maskAllText: false,
      blockAllMedia: false
    }),
    browserTracingIntegration()
  ],
  tracesSampleRate: 0.05,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0
});

const scope = new Scope();
scope.setClient(client);
client.init();

export function captureException(error, hint) {
  scope.captureException(error, hint);
}

/**
 * Error boundary that reports only to the isolated HeatshrinkEditor Sentry client.
 * Drop-in replacement for Sentry.ErrorBoundary scoped to this module.
 */
export class ScopedErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {hasError: false};
  }

  static getDerivedStateFromError() {
    return {hasError: true};
  }

  componentDidCatch(error, errorInfo) {
    captureException(error, {
      captureContext: {
        contexts: {react: {componentStack: errorInfo.componentStack}}
      }
    });
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}
